<script setup lang="ts">
import { asyncComputed } from '@vueuse/core'
import statusStackBar from '@/charts/country/status-stack-bar'
import type { ECharts } from 'echarts'

const props = defineProps<{
    milestoneId?: Dimension['id']
    activityId?: Dimension['id']
    condensed?: boolean
    // eslint-disable-next-line vue/no-unused-properties
    instance?: ECharts
}>()

const emit = defineEmits<{
    (_event: 'update:instance', _value?: ECharts): void
    (_event: 'update:activityId', _value?: Dimension['id']): void
}>()

/**
 * Use query params.
 */
const { config, options } = useParams()

/**
 * Translations.
 */
const { t } = useI18n()

/**
 * Use country values.
 */
const { fetchData } = useValues()

/**
 * Use the framework.
 */
const { fetchChildren, find } = useFramework()

/**
 * Loading activities.
 */
const loadingActivities = ref(false)

/**
 * Loading values.
 */
const loadingValues = ref(false)

/**
 * Loading state.
 */
const loading = computed(() => {
    return loadingValues.value || loadingActivities.value
})

/**
 * List of countries.
 */
const countries = computed(() => {
    return [config.country, config.compareCountry].filter(Boolean)
})

/**
 * Milestone.
 */
const milestone = computed(() => {
    return find(props.milestoneId)
})

/**
 * List of all activities
 */
const activities = asyncComputed(async () => {
    if (!props.milestoneId) return []

    try {
        loadingActivities.value = true
        return (await fetchChildren(props.milestoneId)) as Dimension[]
    } catch (error) {
        console.log(error)
        return []
    } finally {
        loadingActivities.value = false
    }
}, [])

/**
 * Fetch all the values of all activities.
 */
const values = asyncComputed(async () => {
    try {
        loadingValues.value = true
        return await fetchData(unref(activities), unref(countries))
    } catch (error) {
        console.log(error)
        return []
    } finally {
        loadingValues.value = false
    }
}, [])

/**
 * Chart container.
 */
const container = ref<HTMLDivElement>()

/**
 * Get the chart instance.
 */
const { instance: chartInstance } = useChart(container, () => {
    if (unref(loading)) return

    return statusStackBar(
        toValue(milestone)?.name || '',
        unref(values),
        unref(activities),
        options.activityStatuses,
        props.condensed,
        false
    )
})

/**
 * Chart ready callback.
 */
function onChartReady(instance?: ECharts) {
    if (!instance) return

    emit('update:instance', instance)

    instance.on('click', params => {
        const { id } = params.data as { id: string }

        if (id === props.activityId) {
            emit('update:activityId')
        } else {
            emit('update:activityId', id)
        }
    })

    if (toValue(loading)) {
        instance.showLoading()
    }
}

/**
 * Update the chart when the data changes.
 */
watch(loading, loading => {
    if (loading) {
        chartInstance.value?.showLoading()
    } else {
        chartInstance.value?.hideLoading()
    }
})

/**
 * Emit the instance for the parent component.
 */
watch(chartInstance, onChartReady, {
    immediate: true
})
</script>

<template>
    <UiMessage v-if="!activities.length && !loading">
        {{ t('click-to-see-activities') }}
    </UiMessage>
    <div v-else ref="container" class="h-full" />
</template>